import { Trans } from "@lingui/react/macro";
import { OutlinedButton } from "@medoma/components/Button/Button";
import { useParams } from "react-router";
import { z } from "zod";
import styles from "./Communication.module.scss";
import ChatComponent from "./ChatComponent";
import { Heading } from "@medoma/components/Heading/Heading";
import {
  notifyBySMS,
  SMS_NOTIFICATION_MESSAGE_FROM_CHAT,
  usePatient,
} from "@/api/Patients";
import { useMutation } from "@tanstack/react-query";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import type { IExistingPatient, IPatient } from "@medoma/models/patients";
import {
  activeStatuses,
  getPatientFullName,
  patientStatusDictionary,
  patientStatusSchema,
} from "@medoma/models/patients";
import { Text } from "@medoma/components/Text/Text";
import { useLingui } from "@lingui/react/macro";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { localizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";

const hasPhoneNumber = ({
  phoneNumber,
}: Pick<IExistingPatient, "phoneNumber">) => Boolean(phoneNumber);
const isProspectOrAdmitted = ({ status }: Pick<IPatient, "status">) =>
  activeStatuses.some((s) => s === status);
const canSendSms = ({
  phoneNumber,
  status,
}: Pick<IExistingPatient, "phoneNumber" | "status">) =>
  hasPhoneNumber({ phoneNumber }) && isProspectOrAdmitted({ status });

const smsButtonExplanationId = "sms-button-explanation";
const SmsButtonExplanation = ({
  patient,
}: {
  patient: Pick<IExistingPatient, "name" | "phoneNumber" | "status">;
}) => {
  const { t } = useLingui();

  if (!hasPhoneNumber(patient)) {
    return (
      <Text element="p" id={smsButtonExplanationId}>
        <Trans>
          Det går inte att skicka SMS eftersom patienten inte har något
          telefonnummer registrerat
        </Trans>
      </Text>
    );
  }

  if (!isProspectOrAdmitted(patient)) {
    const prospects = t(
      patientStatusDictionary[patientStatusSchema.enum.prospect].plural,
    ).toLocaleLowerCase();
    const admitted = t(
      patientStatusDictionary[patientStatusSchema.enum.admitted].plural,
    ).toLocaleLowerCase();
    return (
      <Text element="p" id={smsButtonExplanationId}>
        <Trans>
          SMS kan endast skickas till patienter som är {prospects} eller{" "}
          {admitted}
        </Trans>
      </Text>
    );
  }

  const patientName = getPatientFullName(patient.name);
  return (
    <Text element="p" id={smsButtonExplanationId}>
      <Trans>
        Vid behov, skicka ett SMS till {patientName}. Meddelandet uppmanar
        mottagaren att öppna och läsa chatten.
      </Trans>
    </Text>
  );
};

export const Communication = () => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { data: patient } = usePatient(patientId);

  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { confirm } = useDialog();

  const { mutate: sendChatNotificationSMS, isPending: isSendingSMS } =
    useMutation({
      mutationFn: () => {
        return notifyBySMS(patientId);
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att skicka SMS.`} ${deducedError(error)}`,
        );
      },
    });

  if (!patient) {
    return null;
  }
  const patientName = getPatientFullName(patient.name);
  const patientPhoneNumber = patient.phoneNumber
    ? localizePhoneNumber({
        countryCode,
        phoneNumber: patient.phoneNumber,
      })
    : // Only used in handleSendChatNotificationSMSClick
      // Which is never called unless button is enabled
      // Which is only enabled if phone number exists
      // So should never happen
      "";

  const handleSendChatNotificationSMSClick = async () => {
    const TWO_LINE_BREAKS = "\n\n";
    const localizedTextMessage = t(SMS_NOTIFICATION_MESSAGE_FROM_CHAT);
    const sendSMSConfirmed = await confirm({
      title: t`Skicka SMS`,
      message: t`Följande SMS skickas till ${patientName} (${patientPhoneNumber}):${TWO_LINE_BREAKS}"${localizedTextMessage}"${TWO_LINE_BREAKS}Vill du skicka det?`,
    });
    if (sendSMSConfirmed) {
      sendChatNotificationSMS();
    } else {
      return;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.chatContainer}>
        <ChatComponent />
      </div>
      <section className={styles.communicationTools}>
        <article>
          <Heading level="h2">
            <Trans>SMS-notis</Trans>
          </Heading>
          <SmsButtonExplanation patient={patient} />
          <OutlinedButton
            aria-describedby={smsButtonExplanationId}
            disabled={isSendingSMS || !canSendSms(patient)}
            onClick={handleSendChatNotificationSMSClick}
          >
            <Trans>Skicka SMS</Trans>
          </OutlinedButton>
        </article>
        <article>
          <Heading level="h2">
            <Trans>Videosamtal</Trans>
          </Heading>
          <Text element="p" id="video-meeting-explanation">
            <Trans>
              Videosamtalet öppnas i en ny flik i din webbläsare. Du kan växla
              mellan webbläsarens flikar som du vill.
            </Trans>
          </Text>
          <OutlinedButton
            aria-describedby="video-meeting-explanation"
            onClick={() =>
              window.open(`/video/${patientId}?video-service=jitsi`, "_blank")
            }
          >
            <Trans>Starta videosamtal</Trans>
          </OutlinedButton>
        </article>
      </section>
    </div>
  );
};
